<template>
	<div
		v-if="quest"
		class="daily-quest-box-rewards"
		:class="[
			{ 'daily-quest-box-rewards--completed': questCompleted },
			{ 'daily-quest-box-rewards--background': background },
		]"
	>
		<div class="daily-quest-box-rewards__content">
			<div class="daily-quest-box-rewards__content--rewards">
				<div class="rewards-label">{{ translate("ui.rewards") }}:</div>
				<div
					class="daily-quest-box-reward"
					v-for="(reward, i) in quest.reward.content"
					:key="i"
				>
					<div class="daily-quest-box-reward__config">
						<AppPrice
							:amount="getAmount(reward.amount)"
							:currency="
								resourceTypeToCurrencyType(
									reward.reward_resource as ResourceType,
								)
							"
							:resourceCheck="false"
							:iconSize="16"
						/>
						<span
							v-if="quest.reward.type === 'RESOURCE_INCOME'"
							class="resource_income"
						>
							{{ `/ ${GAME_TICKER_INTERVAL / 1_000}s` }}
						</span>
					</div>
				</div>
			</div>
			<slot />
		</div>
		<RectangleBoxContainer
			v-if="background"
			cornerTopLeftDisable
			cornerTopRightDisable
		/>
	</div>
</template>

<script lang="ts" setup>
import AppPrice from "@/components/AppPrice.vue";

import RectangleBoxContainer from "@/components/SVG/RectangleBoxContainer.vue";
import { GAME_TICKER_INTERVAL } from "@/config/constants";
import type { ResourceType } from "@/game/entities/config/resources";
import type { QuestActive } from "@/types/quest";
import { resourceTypeToCurrencyType } from "@/utils/formatPrice";
import { getAmount } from "@/utils/number";
import { translate } from "@/utils/translate";

interface Props {
	quest: QuestActive;
	questCompleted: boolean;
	background?: boolean;
}

const {
	quest = null,
	questCompleted = false,
	background = true,
} = defineProps<Props>();
</script>

<style lang="scss" scoped>
.daily-quest-box-rewards {
	width: calc(100% - 2rem);
	height: 3.675rem;
	@include flex-row-center;
	position: absolute;
	bottom: 0rem;

	&--background {
		background-image: linear-gradient(
			to top,
			rgb(37, 38, 42),
			rgb(66, 67, 74)
		);
		&::before {
			position: absolute;
			top: 0;
			left: 0;
			opacity: 0.6;
			mix-blend-mode: color-burn;
			background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
				repeat center;
			background-size: 40%;
			width: 100%;
			height: 100%;
			content: "";
		}

		&.daily-quest-box-rewards--completed {
			margin-bottom: 0;
			box-shadow: inset 0 0 2.375rem 0rem
				rgba($color: #2bad50, $alpha: 0.7);
		}
	}

	.daily-quest-box-reward__config {
		display: flex;
		align-items: center;
		gap: 0.2rem;
	}

	&__content {
		@include flex-row-between;
		align-items: flex-end;
		padding: 0 0.625rem 0.4375rem;
		width: 100%;
		height: 100%;

		&--rewards {
			@include flex-row-start;
			padding-bottom: 0.375rem;
		}
	}

	.rewards-label {
		color: rgb(242, 220, 181);
		font-size: 1rem;
		text-shadow: 0.7px 0.7px 1px rgba(0, 0, 0, 0.6);
		text-transform: uppercase;
	}

	.resource_income {
		color: #f2dcb5;
		line-height: 1rem;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
	}

	:deep(.bidirectional-text-label) {
		filter: drop-shadow(0 0 0.25rem rgba(0, 0, 0, 0.6));
	}

	:deep(.price__value) {
		color: #f2dcb5 !important;
		text-shadow: none;
	}
}
</style>
